import type { Handle } from "remix/ui";
import { reportBoot } from "../lib/boot-timing";
import { on, ref } from "remix/ui";
import { currentHost } from "../config/current-host";
import { splitProductName } from "../config/host";
import { BlobImage } from "../components/blob-image";
import { BrandMark } from "../components/brand-mark";
import { ConfirmSheet } from "../components/confirm-sheet";
import { HomeOptionsSheet } from "../components/home-options-sheet";
import {
  IconMore,
  IconPhone,
  IconPlus,
  IconSettings,
  IconWide,
} from "../components/icons";
import { RenameSheet } from "../components/rename-sheet";
import { StorageMeter } from "../components/storage-meter";
import { downloadBlob, shareOrDownload } from "../lib/media";
import {
  exportEveryProject,
  exportEverythingSummary,
} from "../lib/export-everything";
import {
  loadHomePage,
  loadStorageFigures,
  type HomeLoaderData,
  type ProjectSummary,
  type StorageFigures,
} from "../lib/project-actions";
import {
  backupExtension,
  backupFileAccept,
  BACKUP_IMPORT_READING,
  BackupFormatError,
  backupImportErrorMessage,
  backupImportProgressLabel,
  importKodyVideoBackupFile,
  projectBackupFilename,
  serializeProject,
} from "../lib/project-transfer";
import {
  deleteProject,
  getClipsForProject,
  getProjectAudio,
  getProjectDocumentRecords,
  getProjectText,
  renameProject,
} from "../lib/storage";
import { clearExportCache } from "../lib/export/export-cache";
import { reportError } from "../lib/error-reporting";
import { navigate, onNavigate } from "../router";
import {
  layoutToggleApplies,
  readLayoutMode,
  toggleLayoutMode,
  type LayoutMode,
} from "../lib/layout-mode";
import {
  formatBytes,
  formatStoragePercent,
  storageSeverity,
} from "../lib/storage-space";
import {
  MAX_PROJECTS_TOTAL,
  NEW_PROJECT_ID,
  PROJECT_KIND_LABELS,
  formatDuration,
  projectKind,
  type ClipRecord,
  type ProjectAudioRecord,
} from "../lib/types";

/** Android share targets get flaky well below this; bigger backups download. */
const SHARE_BACKUP_LIMIT_BYTES = 50 * 1024 * 1024;

/**
 * Last loaded home data, kept across mounts: navigating back to home renders
 * the cards immediately (like React Router's blocking loader used to) while
 * a fresh load revalidates underneath. Keyed by tab, so switching back to a
 * tab you have already seen is instant too.
 */
const lastHomeData = new Map<"all", HomeLoaderData>();

export function HomePage(handle: Handle) {
  let data: HomeLoaderData | null = lastHomeData.get("all") ?? null;
  let error: string | null = null;
  let menuProject: ProjectSummary | null = null;
  let renaming: ProjectSummary | null = null;
  let deleting: ProjectSummary | null = null;
  let busy = false;
  let notice: string | null = null;
  /** Restoring a backup: the progress line, then cleared. */
  let restoring: string | null = null;
  let restoreError: string | null = null;
  const backupInputRef: { current: HTMLInputElement | null } = {
    current: null,
  };
  let layout: LayoutMode = readLayoutMode();
  /** Only offered on a desktop-sized window; a phone is already phone-shaped. */
  const showLayoutToggle = layoutToggleApplies();
  // Prefetched when the options sheet opens so the Save-backup tap keeps its
  // user activation (Web Share needs it; an IndexedDB read can outlive it).
  let prefetchedClips: {
    projectId: string;
    clips: Promise<ClipRecord[]>;
    audio: Promise<ProjectAudioRecord | undefined>;
  } | null = null;

  /** Monotonic request id: an older in-flight load (e.g. the mount load
   * resolving after a delete's refresh) must never overwrite newer state. */
  /**
   * The device's space, loaded separately so the cards do not wait for it.
   * Zero and null read as "not known yet", which is what the banner needs:
   * it only appears when space is actually hot.
   */
  let figures: StorageFigures = { storage: null, exportCacheBytes: 0 };

  const loadFigures = () => {
    void loadStorageFigures()
      .then((next) => {
        if (handle.signal.aborted) return;
        figures = next;
        void handle.update();
      })
      // A missing estimate is not worth a message: the banner just stays away.
      .catch(() => undefined);
  };

  let refreshVersion = 0;
  const refresh = () => {
    const version = ++refreshVersion;
    loadFigures();
    void loadHomePage()
      .then((loaded) => {
        if (handle.signal.aborted || version !== refreshVersion) return;
        lastHomeData.set("all", loaded);
        data = loaded;
        error = null;
        void handle.update();
      })
      .catch((err) => {
        if (handle.signal.aborted || version !== refreshVersion) return;
        reportError(err, "load-home");
        error =
          err instanceof Error ? err.message : "Could not load your projects.";
        void handle.update();
      });
  };
  refresh();

  // Revalidate on any navigation back here — a project deleted or renamed
  // elsewhere should not still be showing.
  onNavigate(handle, () => {
    notice = null;
    refresh();
  });

  // Nothing is persisted until the first take/keystroke — backing out of an
  // untouched new project leaves no empty project behind.
  /** Progress line while every project is being packed, or null. */
  let exportingAll: string | null = null;

  /**
   * Pack every project on this device, one file each.
   *
   * Here for the move to a single app: a project has only ever existed in the
   * browser that recorded it, so the files this writes are how they reach the
   * new one — and once this install is retired they are the only copy.
   */
  const exportEverything = () => {
    if (exportingAll !== null || busy) return;
    exportingAll = "Packing…";
    notice = null;
    error = null;
    void handle.update();
    void (async () => {
      try {
        const result = await exportEveryProject(
          async (backup) => {
            await downloadBlob(backup.blob, backup.filename);
            // A breath between downloads: a browser asked to save eleven files
            // in a tight loop blocks the later ones as a popup would be.
            await new Promise((resolve) => setTimeout(resolve, 350));
          },
          (done, total, projectName) => {
            exportingAll = `Packing ${done} of ${total} — ${projectName}…`;
            void handle.update();
          },
        );
        notice = exportEverythingSummary(result);
      } catch (err) {
        error =
          err instanceof Error ? err.message : "Could not export the projects";
      } finally {
        exportingAll = null;
        void handle.update();
      }
    })();
  };

  /**
   * Restore a project from a backup file.
   *
   * This path already existed — but only as a drag-and-drop target, which is
   * unusable on a phone and undiscoverable on a desktop. Same import underneath;
   * the only new thing is a way to reach it.
   *
   * The picked file is imported WHATEVER it is called. `accept` is advisory, and
   * a backup that came off a phone through a share sheet or a download often
   * arrives renamed — refusing it on the extension would look like the feature
   * being broken. A file that is not a backup fails in `parseProjectBackup` with
   * a message written for the user.
   */
  const restoreFromBackup = (file: File) => {
    if (restoring !== null) return;
    restoreError = null;
    restoring = BACKUP_IMPORT_READING;
    void handle.update();
    void (async () => {
      try {
        const project = await importKodyVideoBackupFile(file, (done, total) => {
          restoring = backupImportProgressLabel(done, total);
          void handle.update();
        });
        navigate(`/project/${project.id}`);
      } catch (err) {
        if (!(err instanceof BackupFormatError))
          reportError(err, "import-picker");
        restoreError = backupImportErrorMessage(err);
      } finally {
        restoring = null;
        void handle.update();
      }
    })();
  };

  const openNewProject = () => {
    // Always the unified project. `?kind=` is gone: a project holds video,
    // pictures and cards at once, so there is nothing left for it to select.
    navigate(`/project/${NEW_PROJECT_ID}`);
  };

  // Cached export files (the recoverable last export + scratch) can hold
  // gigabytes — when storage runs hot, the fix must be one tap away.
  const onClearExportCache = () => {
    busy = true;
    error = null;
    notice = null;
    void handle.update();
    void clearExportCache()
      .then((freedBytes) => {
        notice = `Cleared cached export files — freed ${formatBytes(freedBytes)}.`;
        refresh();
      })
      .catch((err) => {
        reportError(err, "clear-export-cache");
        error =
          err instanceof Error
            ? err.message
            : "Could not clear cached exports — try again.";
      })
      .finally(() => {
        busy = false;
        void handle.update();
      });
  };

  const backupProject = (project: ProjectSummary) => {
    void (async () => {
      busy = true;
      error = null;
      notice = null;
      void handle.update();
      try {
        const prefetched = prefetchedClips;
        const usePrefetch =
          prefetched !== null && prefetched.projectId === project.id;
        const clips = usePrefetch
          ? await prefetched.clips
          : await getClipsForProject(project.id);
        if (clips.length === 0) {
          throw new Error(
            `Nothing to back up — this project has no ${
              projectKind(project) === "voice" ? "recordings" : "clips"
            }.`,
          );
        }
        const audio = usePrefetch
          ? await prefetched.audio
          : await getProjectAudio(project.id);
        // Voice notes travel with the audio so a restore is complete.
        const body =
          projectKind(project) === "video"
            ? ""
            : ((await getProjectText(project.id))?.body ?? "");
        const { sections, elements } = await getProjectDocumentRecords(
          project.id,
        );
        const backup = serializeProject(project, clips, audio, body, {
          sections,
          elements,
        });
        const filename = projectBackupFilename(project.name);
        const sizeLabel = formatBytes(backup.size);
        // Android's share sheet fails (often silently) on very large files —
        // route big backups straight to a download instead.
        if (backup.size > SHARE_BACKUP_LIMIT_BYTES) {
          await downloadBlob(backup, filename);
          notice =
            `Backup (${sizeLabel}) saved to your downloads — too large for the share sheet. ` +
            "Drag the file onto this app to restore it.";
        } else {
          const outcome = await shareOrDownload(backup, filename);
          if (outcome !== "cancelled") {
            notice = `Backup (${sizeLabel}) saved. Drag the file onto this app to restore it.`;
          }
        }
      } catch (err) {
        error =
          err instanceof Error ? err.message : "Could not create the backup";
      } finally {
        busy = false;
        void handle.update();
      }
    })();
  };

  return () => {
    if (!data) {
      // Neither a failure NOR a slow load may leave a silent blank screen.
      //
      // Returning null while loading was how this app could show nothing at all:
      // the shell rendered, this rendered nothing inside it, and anything that
      // stopped the load from settling — a blocked IndexedDB upgrade, say — left
      // an empty page with no error, no network activity and nothing in any log
      // to explain it. A load that is merely slow now says so.
      return (
        <div className="screen home-screen">
          {error ? (
            <div className="error-banner">{error}</div>
          ) : (
            <p className="home-loading" aria-live="polite">
              Opening your projects…
            </p>
          )}
        </div>
      );
    }
    // The first screen with content on it — the moment the wait ends.
    reportBoot("home");
    const { projects } = data;
    const { storage, exportCacheBytes } = figures;
    const host = currentHost();
    const brandName = splitProductName(host.brand.productName);

    const severity = storage ? storageSeverity(storage.ratio) : "ok";
    const oldestProject = projects[0] ?? null;
    const limit = MAX_PROJECTS_TOTAL;
    const atLimit = projects.length >= limit;

    return (
      <div className="screen home-screen">
        {/* On mobile, the visible brand/LCP hero lives in index.html
            (#boot-hero) so first paint is not gated on JS — this block is a
            layout spacer there. Desktop hides #boot-hero and shows this. */}
        <div
          className="home-hero home-hero-spacer"
          aria-hidden={
            typeof window !== "undefined" &&
            window.matchMedia("(max-width: 719px)").matches
          }
        >
          <div className="home-hero-art">
            {/* The mark links out to the host's own site. New tab deliberately:
                this is an installable PWA, and navigating the app window away
                to a marketing site is a one-way door on some platforms. */}
            <a
              className="brand-hero-link"
              href={host.webOrigin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${host.brand.orgName} home page`}
            >
              <BrandMark
                size={96}
                className="brand-hero-art"
                variant="camera"
                priority={
                  typeof window !== "undefined" &&
                  window.matchMedia("(max-width: 719px)").matches
                }
              />
            </a>
          </div>
          {/* Must render the same two-tone wordmark as #boot-hero in
              index.html — this is the desktop copy of it, and a mismatch shows
              as the title changing when the phone frame appears. */}
          <h1 className="brand">
            {brandName.lead ? `${brandName.lead} ` : ""}
            <span>{brandName.tail}</span>
          </h1>
          <p className="lede">{host.brand.tagline}</p>
        </div>

        {showLayoutToggle ? (
          <button
            type="button"
            className="home-corner home-corner-left btn-icon"
            aria-label={
              layout === "wide"
                ? "Switch to phone layout"
                : "Switch to wide layout"
            }
            title={layout === "wide" ? "Phone layout" : "Wide layout"}
            mix={on("click", () => {
              layout = toggleLayoutMode();
              void handle.update();
            })}
          >
            {layout === "wide" ? <IconPhone /> : <IconWide />}
          </button>
        ) : null}

        <a
          className="home-corner home-corner-right btn-icon"
          href="/settings"
          aria-label="Settings"
        >
          <IconSettings />
        </a>

        {error ? <div className="error-banner">{error}</div> : null}
        {notice ? <p className="home-notice">{notice}</p> : null}

        {storage && severity !== "ok" ? (
          <div
            className={`storage-banner${severity === "critical" ? " is-critical" : ""}`}
            role="alert"
          >
            <strong>
              Device storage {formatStoragePercent(storage.ratio)} full
              {severity === "critical" ? " — recordings may start failing" : ""}
            </strong>
            <span>
              {formatBytes(storage.usedBytes)} of{" "}
              {formatBytes(storage.quotaBytes)} used.
              {oldestProject
                ? ` Free space fast: delete an old project (⋯ on “${oldestProject.name}”, then Delete).`
                : " Free space by clearing other site data or files on this device."}
            </span>
            {exportCacheBytes > 0 ? (
              <button
                type="button"
                className="btn btn-secondary storage-banner-action"
                disabled={busy}
                mix={on("click", onClearExportCache)}
              >
                Clear cached exports ({formatBytes(exportCacheBytes)})
              </button>
            ) : null}
          </div>
        ) : null}

        <section className="project-slots" aria-label="Projects">
          {projects.map((project) => {
            // Each project's OWN kind, not a tab's. New projects are all one
            // thing; the ones made before this still carry theirs and still open
            // in their own screens, so the label has to come from the record.
            const projectType = projectKind(project);
            return (
              <article
                key={project.id}
                className={`project-slot filled is-${projectType}${
                  project.posterThumb ? " has-poster" : ""
                }`}
              >
                {project.posterThumb ? (
                  <BlobImage
                    blob={project.posterThumb}
                    className="slot-poster"
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                  />
                ) : null}
                <div className="slot-fade" aria-hidden="true" />
                <a className="slot-open" href={`/project/${project.id}`}>
                  {/* Only a LEGACY project is badged. A unified one is just a
                    project, and labelling every card 'Video' would be noise
                    that also happens to be wrong about what is inside. */}
                  {projectType === "video" ? null : (
                    <span className="slot-number">
                      {PROJECT_KIND_LABELS[projectType]}
                    </span>
                  )}
                  <strong>{project.name}</strong>
                  <small>
                    {projectType === "text" ? (
                      <>
                        {project.wordCount} word
                        {project.wordCount === 1 ? "" : "s"}
                        {project.textPreview ? ` · ${project.textPreview}` : ""}
                      </>
                    ) : (
                      <>
                        {project.clipCount}{" "}
                        {projectType === "voice" ? "take" : "clip"}
                        {project.clipCount === 1 ? "" : "s"} ·{" "}
                        {formatDuration(project.durationMs)}
                      </>
                    )}
                  </small>
                </a>
                <button
                  type="button"
                  className="slot-options"
                  aria-label={`Options for ${project.name}`}
                  mix={on("click", () => {
                    // Video backups read clips + music; the other kinds have
                    // nothing worth prefetching for the share-sheet gesture.
                    prefetchedClips =
                      projectType !== "text"
                        ? {
                            projectId: project.id,
                            clips: getClipsForProject(project.id),
                            audio: getProjectAudio(project.id),
                          }
                        : null;
                    menuProject = project;
                    void handle.update();
                  })}
                >
                  <IconMore />
                </button>
              </article>
            );
          })}

          {atLimit ? (
            <p className="project-slot empty locked" role="note">
              <strong>No room for another project</strong>
              <small>{`Delete one of your ${limit} projects to start another.`}</small>
            </p>
          ) : (
            <button
              type="button"
              className="project-slot empty"
              disabled={busy}
              mix={on("click", openNewProject)}
            >
              <span className="slot-plus" aria-hidden="true">
                <IconPlus size={26} />
              </span>
              <strong>New project</strong>
              <small>
                Record clips, add pictures and cards, then publish the film.
              </small>
            </button>
          )}
        </section>

        {/* Restore lives outside the kind tabs on purpose: a backup carries its
            own kind, so which tab is showing has nothing to do with it. */}
        <div className="home-restore">
          <input
            type="file"
            className="visually-hidden"
            accept={backupFileAccept()}
            aria-hidden="true"
            tabIndex={-1}
            mix={[
              ref((node) => {
                backupInputRef.current = node as HTMLInputElement;
              }),
              on("change", (event) => {
                const input = event.currentTarget as HTMLInputElement;
                // Copy before clearing: FileList is live and empties with value=''.
                const file = input.files?.[0] ?? null;
                input.value = "";
                if (file) restoreFromBackup(file);
              }),
            ]}
          />
          <button
            type="button"
            className="home-restore-btn"
            disabled={restoring !== null}
            mix={on("click", () => backupInputRef.current?.click())}
          >
            {restoring ?? "Restore a project from a backup"}
          </button>
          {data.projects.length > 1 ? (
            <button
              type="button"
              className="home-restore-btn home-export-all"
              disabled={exportingAll !== null || restoring !== null}
              aria-label="Export every project on this device"
              mix={on("click", () => exportEverything())}
            >
              {exportingAll ?? `Export all ${data.projects.length} projects`}
            </button>
          ) : null}
          {restoreError ? (
            <p className="home-restore-error" role="alert">
              {restoreError}
            </p>
          ) : (
            <p className="home-restore-hint muted">
              Opens a <code>{backupExtension()}</code> file as a new project —
              including one exported from another device.
            </p>
          )}
        </div>

        <p className="home-privacy">
          <span>Your work stays on this device until you publish.</span>
          {storage ? <StorageMeter storage={storage} /> : null}
        </p>

        {menuProject ? (
          <HomeOptionsSheet
            projectName={menuProject.name}
            canBackup={projectKind(menuProject) !== "text"}
            onClose={() => {
              menuProject = null;
              void handle.update();
            }}
            onOpen={() => {
              const id = menuProject!.id;
              menuProject = null;
              void handle.update();
              navigate(`/project/${id}`);
            }}
            onRename={() => {
              renaming = menuProject;
              menuProject = null;
              void handle.update();
            }}
            onBackup={() => {
              const project = menuProject!;
              menuProject = null;
              void handle.update();
              backupProject(project);
            }}
            onDelete={() => {
              deleting = menuProject;
              menuProject = null;
              void handle.update();
            }}
          />
        ) : null}

        {renaming ? (
          <RenameSheet
            key={renaming.id}
            initialName={renaming.name}
            onClose={() => {
              renaming = null;
              void handle.update();
            }}
            onSave={async (name) => {
              await renameProject(renaming!.id, name);
              refresh();
            }}
          />
        ) : null}

        {deleting ? (
          <ConfirmSheet
            title={
              projectKind(deleting) === "text"
                ? "Delete note?"
                : "Delete project?"
            }
            message={`Delete “${deleting.name}”${
              projectKind(deleting) === "text"
                ? ""
                : projectKind(deleting) === "voice"
                  ? " and all its recordings"
                  : " and everything in it"
            }? This can’t be undone.`}
            confirmLabel="Delete"
            onClose={() => {
              deleting = null;
              void handle.update();
            }}
            onConfirm={async () => {
              await deleteProject(deleting!.id);
              refresh();
            }}
          />
        ) : null}
      </div>
    );
  };
}
